import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import type { Language } from '../../../translations'
import type { Message } from '../types'
import { SUGGEST_URL } from '../constants'

interface UseChatSuggestionsOptions {
    input: string
    setInput: Dispatch<SetStateAction<string>>
    lang: Language
    messages: Message[]
}

interface UseChatSuggestionsResult {
    chatSuggestions: string[]
    isSuggestOpen: boolean
    suggestIndex: number
    setSuggestIndex: Dispatch<SetStateAction<number>>
    setIsSuggestOpen: Dispatch<SetStateAction<boolean>>
    handleInputChange: (value: string) => void
    handleInputFocus: () => void
    handleInputBlur: () => void
    handleInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>, onSend: () => void) => void
    acceptChatSuggestion: (value: string) => void
    inputRef: React.MutableRefObject<HTMLInputElement | null>
}

export function useChatSuggestions({ input, setInput, lang, messages }: UseChatSuggestionsOptions): UseChatSuggestionsResult {
    const [remoteChatSuggestions, setRemoteChatSuggestions] = useState<string[]>([])
    const [isSuggestOpen, setIsSuggestOpen] = useState(false)
    const [suggestIndex, setSuggestIndex] = useState(-1)

    const inputRef = useRef<HTMLInputElement | null>(null)
    const suggestAbortRef = useRef<AbortController | null>(null)
    const suggestTimerRef = useRef<number | null>(null)

    const chatSuggestions = useMemo(() => {
        const query = input.trim().toLowerCase()
        if (!query) return []

        const seen = new Set<string>()
        const matches: string[] = []

        for (const suggestion of remoteChatSuggestions) {
            const value = (suggestion || '').trim()
            if (!value) continue
            const haystack = value.toLowerCase()
            if (haystack === query) continue
            if (seen.has(haystack)) continue
            if (haystack.startsWith(query) || haystack.includes(query)) {
                seen.add(haystack)
                matches.push(value)
            }
            if (matches.length >= 7) return matches
        }

        for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
            const msg = messages[idx]
            if (!msg || msg.role !== 'user') continue
            const raw = (msg.content || '').trim()
            if (!raw) continue
            const haystack = raw.toLowerCase()
            if (haystack === query) continue
            if (seen.has(haystack)) continue
            if (haystack.startsWith(query) || haystack.includes(query)) {
                seen.add(haystack)
                matches.push(raw)
            }
            if (matches.length >= 7) break
        }

        return matches
    }, [input, messages, remoteChatSuggestions])

    useEffect(() => {
        const q = input.trim()
        if (!q || q.length < 2) {
            setRemoteChatSuggestions([])
            return
        }

        if (suggestTimerRef.current) {
            window.clearTimeout(suggestTimerRef.current)
        }

        suggestTimerRef.current = window.setTimeout(async () => {
            try {
                if (suggestAbortRef.current) {
                    suggestAbortRef.current.abort()
                }

                const controller = new AbortController()
                suggestAbortRef.current = controller

                const url = new URL(SUGGEST_URL)
                url.searchParams.set('q', q)
                url.searchParams.set('lang', lang)

                const response = await fetch(url.toString(), { signal: controller.signal })
                if (!response.ok) return
                const data = await response.json()
                const suggestions = Array.isArray(data?.suggestions) ? data.suggestions : []
                setRemoteChatSuggestions(suggestions.filter((item: unknown) => typeof item === 'string'))
            } catch (error) {
                if ((error as Error)?.name === 'AbortError') return
                setRemoteChatSuggestions([])
            }
        }, 180)

        return () => {
            if (suggestTimerRef.current) {
                window.clearTimeout(suggestTimerRef.current)
            }
        }
    }, [input, lang])

    useEffect(() => () => {
        if (suggestAbortRef.current) {
            suggestAbortRef.current.abort()
        }
    }, [])

    const handleInputChange = useCallback((value: string) => {
        setInput(value)
        setIsSuggestOpen(true)
        setSuggestIndex(-1)
    }, [setInput])

    const handleInputFocus = useCallback(() => {
        if (chatSuggestions.length > 0) {
            setIsSuggestOpen(true)
        }
    }, [chatSuggestions.length])

    const handleInputBlur = useCallback(() => {
        window.setTimeout(() => setIsSuggestOpen(false), 120)
    }, [])

    const acceptChatSuggestion = useCallback((value: string) => {
        handleInputChange(value)
        setIsSuggestOpen(false)
        inputRef.current?.focus()
    }, [handleInputChange])

    const handleInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>, onSend: () => void) => {
        const hasSuggestions = isSuggestOpen && chatSuggestions.length > 0

        if (hasSuggestions && event.key === 'ArrowDown') {
            event.preventDefault()
            setSuggestIndex(index => Math.min(index + 1, chatSuggestions.length - 1))
            return
        }

        if (hasSuggestions && event.key === 'ArrowUp') {
            event.preventDefault()
            setSuggestIndex(index => Math.max(index - 1, -1))
            return
        }

        if (hasSuggestions && event.key === 'Escape') {
            event.preventDefault()
            setIsSuggestOpen(false)
            setSuggestIndex(-1)
            return
        }

        if (event.key === 'Enter') {
            if (hasSuggestions && suggestIndex >= 0) {
                event.preventDefault()
                acceptChatSuggestion(chatSuggestions[suggestIndex])
                return
            }
            onSend()
        }
    }, [acceptChatSuggestion, chatSuggestions, isSuggestOpen, suggestIndex])

    return {
        chatSuggestions,
        isSuggestOpen,
        suggestIndex,
        setSuggestIndex,
        setIsSuggestOpen,
        handleInputChange,
        handleInputFocus,
        handleInputBlur,
        handleInputKeyDown,
        acceptChatSuggestion,
        inputRef
    }
}
